// React Dependencies
import React, { useState, useEffect } from 'react';

// External Dependencies
import _, { lowerCase } from 'lodash';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Button, Card, TextField, RadioGroup, Radio, FormControlLabel, Checkbox, makeStyles, Icon } from '@material-ui/core';
import { Link } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import { toast } from 'react-toastify';

// Internal Dependencies
import Photo from './Photo';
import MaterialDatePicker from '../../../global/form/MaterialDatePicker';
import Colours from '../../../../styles/colours';
import apiService from '../../../../services.js/apiService';
import OverLoading from '../../../global/OverLoading';
import { Status, Url } from '../../../../common/constants';
import { IsEmpty, IsNumber } from '../../../../common/checks';
import { ConvertDateToSGTime, ConvertEndDate, ConvertStartDate } from '../../../../common/util';
import MText from '../../../common/MText';
import RadioBtnSelect from '../../../common/radioBtnSelect';
import MonthDayValidity from './MonthDayValidity';

const size = {
  12: '0.96rem',
  14: '1.077rem',
  16: '1.2rem',
  24: '1.846rem',
  28: '2.154rem',
};

const useStyles = makeStyles(({
  alterDateContainer: {
    width: '50%',

    '@media (max-width: 600px)': {
      width: '100%',
    },
  },
  chip: {
    border:       'solid',
    borderColor:  Colours.Primary,
    borderRadius: '30,',
    borderWidth:  1.5,
    fontSize:     15,
  },
  paper: {
    flexGrow: 1,
    margin:   'auto',
    maxWidth: '100%',
    padding:  '20px 30px',
  },
  textHeader: {
    color:    Colours.Black,
    fontSize: size[16],
  },
  dateHeader: {
    fontSize: size[16],
  },
  subtext: {
    color:    Colours.Black,
    fontSize: size[12],
  },
}));

const Gift = (props) => {
  const classes = useStyles();

  const { pageId, giftId } = props.match.params;

  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [extendedExpiryDate, setExtendedExpiryDate] = useState(null);
  const [issuableQuantity, setIssuableQuantity] = useState('');
  const [termsAndCondition, setTermsAndCondition] = useState('');
  const [status, setStatus] = useState(Status.Draft);
  const [isHqAuthToIssue, setIsHqAuthToIssue] = useState(false);
  const [validityInMonths, setValidityInMonths] = useState(0);
  const [validityInDays, setValidityInDays] = useState(0);
  const [validitySetByStore, setValiditySetByStore] = useState(true);
  const [pauseIssuance, setPauseIssuance] = useState(false);
  const [type, setType] = useState(null);
  const [images, setImages] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isValidityByDateRange, setIsValidityByDateRange] = useState(true);

  const getGiftDataHandler = async () => {
    try {
      setLoading(true);
      const gift = await apiService.getGift({ giftId, pageId });

      setDescription(gift.label);
      setStartDate(!IsEmpty(gift.startDate) ? ConvertDateToSGTime(new Date(gift.startDate)) : null);
      setEndDate(!IsEmpty(gift.endDate) ? ConvertDateToSGTime(new Date(gift.endDate)) : null);
      setExtendedExpiryDate(!IsEmpty(gift.extendedEndDate) ? ConvertDateToSGTime(new Date(gift.extendedEndDate)) : null);
      setIssuableQuantity(gift.issuableQty || '');
      setTermsAndCondition(gift.terms || '');
      setStatus(gift.status);
      setIsHqAuthToIssue(gift.isHqAuthToIssue || false);
      setPauseIssuance(gift.pauseIssuance || false);
      setType(gift.type);
      setImages(gift.images);
      setValidityInMonths(gift.validityInMonths);
      setValidityInDays(gift.validityInDays);
      setValiditySetByStore(gift.isValidityInDaysSetByStore);
      setData(gift);

      if (IsNumber(gift.validityInDays) || IsNumber(gift.validityInMonths)) {
        setIsValidityByDateRange(false);
      } else {
        setIsValidityByDateRange(true);
      }
    } catch (err) {
      console.log('Error in getGiftDataHandler: ', err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const saveGiftDataHandler = async () => {
    try {
      setLoading(true);
      let issuableQty = null;
      if (!IsEmpty(issuableQuantity)) {
        issuableQty = Number(issuableQuantity) <= 0 ? 0 : Number(issuableQuantity);
      }
      const payload = {
        label:                      description,
        startDate:                  (!IsEmpty(startDate) && isValidityByDateRange) ? ConvertStartDate(startDate) : null,
        endDate:                    (!IsEmpty(endDate) && isValidityByDateRange) ? ConvertEndDate(endDate) : null,
        extendedEndDate:            (!IsEmpty(extendedExpiryDate) && isValidityByDateRange) ? ConvertEndDate(extendedExpiryDate) : null,
        terms:                      termsAndCondition,
        type,
        issuableQty,
        validityInMonths:           isValidityByDateRange ? null : (validityInMonths || 0),
        validityInDays:             isValidityByDateRange ? null : (validityInDays || 0),
        isValidityInDaysSetByStore: isValidityByDateRange ? true : validitySetByStore, // Default value of isValidityInDaysSetByStore is true
        status,
        isHqAuthToIssue,
        pauseIssuance,
        images,
      };

      if (status === Status.Activated && IsEmpty(description)) {
        return toast.error(`Please provide a name for the ${lowerCase(type)}`);
      }

      if (isValidityByDateRange) {
        if ((!IsEmpty(startDate) && !IsEmpty(endDate)) && moment(endDate).isSameOrBefore(moment(startDate))) {
          return toast.error('End date should not exceed the start date!');
        } else if ((!IsEmpty(endDate) && !IsEmpty(extendedExpiryDate)) && moment(extendedExpiryDate).isSameOrBefore(moment(endDate))) {
          return toast.error('Extended Expiry date should not exceed the end date!');
        }
        if (status === Status.Activated && (IsEmpty(startDate) || IsEmpty(endDate))) {
          return toast.error(`Please provide the start and end dates the ${lowerCase(type)} is valid for`);
        }
      } else {
        if (!IsEmpty(validityInMonths) && validityInMonths < 0) {
          return toast.error(`Invalid value for 'months'`);
        }
        if (!IsEmpty(validityInMonths) && validityInDays < 0) {
          return toast.error(`Invalid value for 'days'`);
        }
        if (status === Status.Activated && (IsEmpty(validityInMonths) || validityInMonths === 0) && (IsEmpty(validityInDays) || validityInDays === 0)) {
          return toast.error('Validity in months and days cannot be both 0');
        }
      }

      if (payload.status === Status.Activated && !IsEmpty(payload.issuableQty) && payload.issuableQty <= 0) {
        return toast.error('Issuable quantity must be more than 0');
      }
      if (payload.status === Status.Activated && _.isEmpty(images)) {
        return toast.error(`To activate the ${lowerCase(type)}, please upload at least one image`);
      }

      await apiService.updateGift({ giftId, pageId }, payload);
      toast.success('Gift updated successfully');

      await getGiftDataHandler(); // get updated data
    } catch (err) {
      console.log('Error in saveGiftDataHandler: ', err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    const newDate = new Date(date);
    return moment(newDate).format('YYYY-MM-DD');
  };

  useEffect(() => {
    getGiftDataHandler();
  }, []);

  return (
    <div className="container-fluid">
      { loading && <OverLoading /> }
      <div className="m-0 mt-md-4 ml-md-2 mb-md-2">
        <div className="d-inline-flex align-items-center mb-3">
          <Link to={Url.VouchersAndCoupons.replace(':pageId', pageId)} title="Back" className="d-flex align-items-center">
            <Icon className="back" style={{ fontSize: size[28] }}>arrow_back</Icon>
          </Link>
          <MText
            size="1.462rem"
            variant="h4"
            bold
            text={`Edit ${type}`}
          />
        </div>

        <Card className="px-3 px-md-4 pt-2 pb-4">
          <MText
            className="mt-2"
            variant="body2"
            style={{ color: '#8B8989', fontSize: size[14] }}
            text={(
              <span>
                {type}
                {' '}
                ID:
                {' '}
                {giftId}
              </span>
            )}
          />

          <div style={{ marginTop: size[24], marginBottom: size[24] }}>
            <MText size={size[16]} variant="subtitle1" color="inherit" className="mt-3 mb-1" text={`Name of ${lowerCase(type)}`} />
            <MText colour={Colours.Gray} text="This name will be displayed to the customer" />
            <TextField
              className="mt-1"
              value={description || ''}
              onChange={(e) => setDescription(e.currentTarget.value)}
              variant="outlined"
              size="small"
              disabled={data && data.status === Status.Deactivated}
              inputProps={{
                style: {
                  fontSize: size[14],
                  width:    '22.154rem',
                },
              }}
            />
            <hr className="mt-4" />

            <div style={{ marginTop: size[24], marginBottom: size[24] }}>
              <MText size={size[16]} variant="subtitle1" color="inherit" className="mt-3 mb-1" text="Validity" />
              <MText colour={Colours.Gray} text={`Select either option for the ${lowerCase(type)} expiry:`} />
              <div className="row pl-2 mt-1">
                <div className="col-12 col-lg-6 col-xl-6 mb-2">
                  <RadioBtnSelect
                    options={[{
                      label:    'Start and end on specific date',
                      subLabel: '(Choose this if your voucher is only valid for a fixed period)',
                      value:    true,
                    }]}
                    value={isValidityByDateRange}
                    onChange={() => setIsValidityByDateRange(true)}
                    disabled={data && data.status !== Status.Draft}
                  />
                  <div className="ml-4">
                    <MText text="Start Date" />
                    <MaterialDatePicker
                      minDate={false}
                      inputVariant="outlined"
                      value={startDate}
                      disabled={(data && data.status !== Status.Draft) || !isValidityByDateRange}
                      onChange={(e) => setStartDate(e ? formatDate(e.toString('yyyy-MM-dd')) : null)}
                      clearable
                      inputStyle={{ padding: '10.5px 14px', fontSize: size[12], width: '18rem' }}
                      className="mt-1 mb-0"
                      isRootStyleEnabled
                    />

                    <MText className="mt-2" text="End Date" />
                    <MaterialDatePicker
                      minDate={!startDate ? false : new Date(startDate)}
                      inputVariant="outlined"
                      value={endDate}
                      disabled={(data && data.status !== Status.Draft) || !isValidityByDateRange}
                      onChange={(e) => setEndDate(e ? formatDate(e.toString('yyyy-MM-dd')) : null)}
                      clearable
                      inputStyle={{ padding: '10.5px 14px', fontSize: size[12], width: '18rem' }}
                      className="mt-1 mb-0"
                      isRootStyleEnabled
                    />

                    {
                      (data && data.status !== Status.Draft && isValidityByDateRange) && (
                        <>
                          <MText className="mt-3" size={size[14]} text="Extended Expiry Date" />
                          <MaterialDatePicker
                            minDate={!endDate ? false : new Date(endDate)}
                            inputVariant="outlined"
                            value={extendedExpiryDate}
                            disabled={(data && data.status !== Status.Activated) || !isValidityByDateRange}
                            onChange={(value) => setExtendedExpiryDate(value ? formatDate(value.toString('yyyy-MM-dd')) : null)}
                            clearable
                            inputStyle={{ padding: '10.5px 14px', fontSize: size[12], width: '18rem' }}
                            style={{ borderRadius: '5px' }}
                            className="mt-1 mb-0"
                            isRootStyleEnabled
                          />
                        </>
                      )
                    }
                  </div>
                </div>

                <div className="col-12 col-lg-6 col-xl-6 mt-2 mt-lg-0">
                  <RadioBtnSelect
                    options={[{
                      label:    'Expiry date calculated on issuance',
                      subLabel: `(Choose this if the voucher's expiry date is calculated on the day it is issued)`,
                      value:    true,
                    }]}
                    value={!isValidityByDateRange}
                    onChange={() => setIsValidityByDateRange(false)}
                    disabled={data && data.status !== Status.Draft}
                  />
                  <div className="ml-4">
                    <MonthDayValidity
                      validityInMonths={validityInMonths}
                      validityInDays={validityInDays}
                      setValidityInMonths={setValidityInMonths}
                      setValidityInDays={setValidityInDays}
                      disabled={isValidityByDateRange}
                      textSize={size[12]}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12 mt-3">
              <MText text={`* Note: once ${lowerCase(type)} has been activated, the selection cannot be changed`} />
            </div>
            <hr className="mt-4" />

            <MText size={size[16]} variant="subtitle1" color="inherit" className="mt-3 mb-1" text="Issuable Quantity" />
            <MText colour={Colours.Gray} text={`Limit the number of ${lowerCase(type)}s issuable`} />
            <TextField
              className="mt-1"
              value={issuableQuantity || ''}
              onChange={(e) => setIssuableQuantity(e.currentTarget.value)}
              variant="outlined"
              size="small"
              type="number"
              disabled={data && data.status === Status.Deactivated}
              inputProps={{
                min:   1,
                style: {
                  fontSize: '12px',
                  width:    '12.75rem',
                },
              }}
            />

            {
              (data && data.status === Status.Activated) && (
                <>
                  <MText size={size[16]} variant="subtitle1" color="inherit" className="mt-4 mb-0" text="Pause Issuance" />
                  <MText
                    variant="body1"
                    className="m-0"
                    text="Check this box to stop new vouchers from being issued"
                  />
                  <FormControlLabel
                    className="ml-2"
                    control={<Checkbox checked={pauseIssuance} onChange={(e) => setPauseIssuance(e.target.checked)} name="gilad" />}
                    label={<MText text={`Pause issuance of voucher ${pauseIssuance ? '(Uncheck to resume issuance)' : ''}`} />}
                  />
                </>
              )
            }

            <MText size={size[16]} variant="subtitle1" color="inherit" className={`mt-4 mb-0 ${classes.textHeader}`} text="HQ Issuance" />
            <FormControlLabel
              className={`ml-2 ${classes.textHeader}`}
              control={<Checkbox checked={isHqAuthToIssue} onChange={(e) => setIsHqAuthToIssue(e.target.checked)} name="gilad" />}
              label={<MText text="HQ business staff (admin and editor) can issue voucher" />}
            />

            <hr className="mt-4" />

            <MText size={size[16]} className="mt-4 mb-1" text={`${type} Photo`} />
            <Photo
              giftId={giftId}
              gift={data}
              onSort={(image) => { setImages(image); }}
              onUpdateImages={setImages}
              page={{ id: pageId }}
              textSize={size[12]}
            />
            <hr className="mt-5" />

            <MText size={size[16]} className="mt-4 mb-1" text="Terms & Conditions" />
            <TextField
              className="mt-1 col-12 col-xl-8"
              multiline
              rows={4}
              value={termsAndCondition || ''}
              onChange={(e) => setTermsAndCondition(e.currentTarget.value)}
              variant="outlined"
              size="small"
              disabled={data && data.status === Status.Deactivated}
              inputProps={{
                style: {
                  fontSize: '12px',
                  width:    isMobile ? '18rem' : '26rem',
                },
              }}
            />
            <hr className="mt-4" />

            <MText size={size[16]} className="my-0 mt-3" text="Status" />
            <div className="ml-2 mb-1">
              <RadioGroup row={!isMobile} name="giftType" value={status} onChange={(e) => setStatus(e.target.value)}>
                <FormControlLabel
                  className="mb-0"
                  value={Status.Draft}
                  control={<Radio color="primary" />}
                  label={<MText text={Status.Draft} />}
                />
                <FormControlLabel
                  className="mb-0"
                  value={Status.Activated}
                  control={<Radio color="primary" />}
                  label={(
                    <MText
                      text={(
                        <span>
                          {Status.Activated}
                          {' '}
                          <span style={{ color: Colours.Red }}>*</span>
                        </span>
                      )}
                    />
                  )}
                />
                <FormControlLabel
                  className="mb-0"
                  value={Status.Deactivated}
                  control={<Radio color="primary" />}
                  label={<MText text={Status.Deactivated} />}
                />
              </RadioGroup>

              <span style={{ fontSize: '12px' }}>
                <span style={{ color: Colours.Red }}>*</span>
                {' '}
                Once activated, you will not be able to change the start and expiry dates
              </span>
            </div>
          </div>

          <Button className="ml-3" variant="contained" color="secondary" style={{ width: '7rem' }} onClick={saveGiftDataHandler}>Save</Button>
        </Card>
      </div>
    </div>
  );
};

Gift.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      pageId: PropTypes.string,
      giftId: PropTypes.string,
    }).isRequired,
  }).isRequired,

  pageInView: PropTypes.shape({
    id:   PropTypes.number,
    name: PropTypes.string,
  }),
};

Gift.defaultProps = {
  pageInView: {
    id:   null,
    name: null,
  },
};

export default Gift;
